import { ActionButton, FormInputRow } from '@emerald-works-nova/components'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { Divider, Grid } from '@mui/material'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { BannerStatic } from '../../../../components'
import { TenantEditSlice, UserDeleteSlice } from '../../../../reducers'
import Header from '../header'
import UploadInput from './upload-input'
import ConfirmationModal from './confirmation-modal'

const requiredKeys = ['s3Key']

const UserDelete = ({ onClickCancel, onUploadStart }) => {
  const { pk: tenantId, name, clientName } = useSelector(TenantEditSlice.selectors.tenant)

  const form = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: { s3Key: '' }
  })

  const { handleSubmit, setValue, reset, watch } = form

  const [userBulkDelete] = useEvent([
    UserDeleteSlice.eventBus.userBulkDelete
  ])

  useEventsOnViewLoad(() => {
    userBulkDelete.registerAdhocOnStartListener(() => onUploadStart())
    userBulkDelete.registerAdhocOnErrorListener(() => {
      const label = 'An unexpected error ocurred while deleting the users.'
      BannerStatic.show({ label, isError: true })
    })
    userBulkDelete.registerAdhocOnSuccessListener(
      ({ payload: { failed, warnings } }) => {
        const hasErrors = failed.length > 0 || warnings.length > 0
        const banner = hasErrors ? {
          label: `Users Bulk Deletion at ${clientName ?? name} has finished with errors`,
          isWarning: true
        } : {
          label: `Users Bulk Deletion at ${clientName ?? name} has finished`
        }
        BannerStatic.show(banner)
      })
  }, [userBulkDelete])

  const [isModalOpen, setModalOpen] = useState(false)
  const [pendingFormData, setPendingFormData] = useState(null)

  const onSubmit = data => {
    setPendingFormData(data)
    setModalOpen(true)
  }

  const onConfirm = () => {
    if (pendingFormData) {
      userBulkDelete.trigger({ ...pendingFormData, tenantId })
      setModalOpen(false)
      setPendingFormData(null)
    }
  }

  const handleClickCancel = () => {
    reset()
    onClickCancel()
  }

  const handleCloseModal = () => setModalOpen(false)

  const handleFileAccept = ({ s3Key }) => setValue('s3Key', s3Key)

  const values = watch()
  const isLoading = userBulkDelete.isWorking
  const isValid = requiredKeys.every(key => !!values[key])

  return (
    <Grid container>
      <ConfirmationModal open={isModalOpen} onClose={handleCloseModal} onConfirm={onConfirm} />
      <Header
        title='Delete Users'
        actions={
          <>
            <Grid item>
              <ActionButton
                variant='secondary'
                dataTest='user-delete-cancel-button'
                onClick={handleClickCancel}
              >
                Cancel
              </ActionButton>
            </Grid>
            <Grid item>
              <ActionButton
                variant='primary'
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid}
                isWorking={isLoading}
                dataTest='user-delete-upload-button'
              >
                Delete Users
              </ActionButton>
            </Grid>
          </>
        }
      />
      <Divider width='100%' />
      <Grid item xs={12}>
        <FormInputRow
          title='File Upload'
          description='Upload a CSV UTF-8 (Comma delimited) file that contains am Email and/or Username.'
          smallFormTitles
          InputComponent={<UploadInput onAccept={handleFileAccept} />}
        />
      </Grid>
    </Grid>
  )
}

export default UserDelete
